// Imports => Vendor
import axios from "axios";

// Imports => MOBX
import { action, computed, makeObservable, observable } from "mobx";

// Imports => Constants
import { KEYS } from "@opp/ui-kit";

// Imports => Models
import { TransactionModel } from "@models/transaction.model";

// Imports => Utilities
import { AcAutoLoad, AcAutoSave } from "@utils";
import {
  AcFormatErrorMessage,
  AcFormatRequestParameters,
  AcIsNull,
  AcIsSet,
  AcIsUndefined,
  AcRemoveState,
  AcSaveState,
} from "@opp/ui-kit";

const _default = {
  options: {},
  meta: null,
  transaction: null,
  tracking: null,
  changelog: null,
  refundable: null,
  payouts: null,
  refund_payouts: null,
  refund_transaction: null,
};

// this is to avoid wrapping everything into mobx HOC since we already have this dirty hack
export let app = {} as any;

export class TransactionsStore {
  constructor(store) {
    makeObservable(this);

    AcAutoLoad(this, KEYS.TRANSACTIONS);
    AcAutoSave(this);

    app.store = store;
  }

  @observable
    meta = _default.meta;

  @observable
    transaction = _default.transaction;

  @observable
    tracking = _default.tracking;

  @observable
    changelog = _default.changelog;

  @observable
    refundable = _default.refundable;

  @observable
    payouts = _default.payouts;

  @observable
    refund_payouts = _default.refund_payouts;

  @observable
    refund_transaction = _default.refund_transaction;

  @computed
  get current_transaction() {
    return this.transaction;
  }

  @computed
  get current_tracking() {
    return this.tracking;
  }

  @computed
  get current_changelog() {
    return this.changelog;
  }

  @computed
  get current_refundable() {
    return this.refundable;
  }

  @computed
  get current_payouts() {
    return this.payouts;
  }

  @computed
  get current_refund_payouts() {
    return this.refund_payouts;
  }

  @computed
  get current_refund_transaction() {
    return this.refund_transaction;
  }

  @observable
    loading = {
      status: false,
      message: null,
    };

  @observable
    loading_refundable = {
      status: false,
      message: null,
    };

  @observable
    loading_payouts = {
      status: false,
      message: null,
    };

  @observable
    loading_refund_payouts = {
      status: false,
      message: null,
    };

  @observable
    loading_refund_transaction = {
      status: false,
      message: null,
    };

  @computed
  get is_loading() {
    return this.loading.status;
  }

  @computed
  get is_loading_refundable() {
    return this.loading_refundable.status;
  }

  @computed
  get is_loading_payouts() {
    return this.loading_payouts.status;
  }

  @computed
  get is_loading_refund_payouts() {
    return this.loading_refund_payouts.status;
  }

  @computed
  get is_loading_refund_transaction() {
    return this.loading_refund_transaction.status;
  }

  @action
    setLoading = (state, message) => {
      this.loading = {
        status: state || false,
        message,
      };
    };
  @action
    setLoadingRefundable = (state, message) => {
      this.loading_refundable = {
        status: state || false,
        message,
      };
    };

  @action
    setLoadingPayouts = (state, message) => {
      this.loading_payouts = {
        status: state || false,
        message,
      };
    };

  @action
    setLoadingRefundPayouts = (state, message) => {
      this.loading_refund_payouts = {
        status: state || false,
        message,
      };
    };

  @action
    setLoadingRefundTransaction = (state, message) => {
      this.loading_refund_transacstion = {
        status: state || false,
        message,
      };
    };

  @observable
    busy = {
      status: false,
      message: null,
    };

  @computed
  get is_busy() {
    return this.busy.status;
  }

  @action
    setBusy = (state, message) => {
      this.busy = {
        status: state || false,
        message,
      };
    };

  @action
    get_by_id = (t, id, options) => {
      this.setLoading(true);

      const params = AcFormatRequestParameters(this, options, true);

      return app.store.api.transactions
        .get_by_id(id, params)
        .then(async (response) => {
        // Get parent payment method.
        // First check if we have it in our storage
        // Else get it via API
          let current_movements = app.store.movements.current_movements;
          let parent_payment_method = null;
          let found_but_null = false;

          current_movements.every((movement) => {
            if (movement.uid === response.uid) {
              if (movement.parent_payment_method === null) {
                found_but_null = true;
              } else {
                parent_payment_method = movement.parent_payment_method;
              }
              return false;
            }

            return true;
          });

          if (!found_but_null && parent_payment_method === null) {
            await app.store.api.movements
              .get_parent_payment_methods([response.uid])
              .then(async (response) => {
                if (response.length === 1) {
                  parent_payment_method = response[0].parent_payment_method;
                }
              })
              .catch((error) => {
              // Payment method probably shows multi. Not the biggest issue
                console.log(error);
              });
          }

          const formatted = new TransactionModel(response, parent_payment_method);
          await this.set(KEYS.TRANSACTION, formatted);

          this.setLoading(false);

          return formatted;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            app.store.toasters.add({
              variant: "error",
              title: `${t("transactions.failed-to-retrieve-with-id", {
                ns: "store",
              })} '<strong>${id}</strong>'`,
              description: AcFormatErrorMessage(error),
            });
          }

          this.setLoading(false);

          if (!axios.isCancel(error)) {
            throw error;
          }
        });
    };

  @action
    get_tracking = (t, id) => {
      this.setLoading(true);

      return app.store.api.transactions
        .get_tracking(id)
        .then(async (response) => {
          await this.set(KEYS.TRACKING, response);

          this.setLoading(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error))
            app.store.toasters.add({
              variant: "error",
              title: t("transactions.failed-to-retrieve-tracking", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
            });

          this.setLoading(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    get_changelog = (t, id) => {
      this.setLoading(true);

      return app.store.api.transactions
        .get_changelog(id)
        .then(async (response) => {
          await this.set(KEYS.CHANGELOG, response);

          this.setLoading(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error))
            app.store.toasters.add({
              variant: "error",
              title: t("transactions.failed-to-retrieve-changelog", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
            });
          this.setLoading(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    store_refund = (t, uid, data) => {
      this.setBusy(true);

      return app.store.api.transactions
        .store_refund(uid, data)
        .then(async (response) => {
          app.store.toasters.add({
            variant: "success",
            title: t("transactions.refund-success", { ns: "store" }),
          });

          this.setBusy(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            let message = t("transactions.refund-failed", { ns: "store" });

            if (
              error.response &&
            error.response.data &&
            error.response.data.core &&
            error.response.data.core.error &&
            error.response.data.core.error.message
            ) {
              message = error.response.data.core.error.message;
            }
            app.store.toasters.add({
              variant: "error",
              title: t("transactions.refund-failed", { ns: "store" }),
              description: message,
            });
          }
          this.setBusy(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    store_topup = (t, uid, data) => {
      this.setBusy(true);

      return app.store.api.transactions
        .store_topup(uid, data)
        .then(async (response) => {
          app.store.toasters.add({
            variant: "success",
            title: t("transactions.topup-success", { ns: "store" }),
          });

          this.setBusy(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error))
            app.store.toasters.add({
              variant: "error",
              title: t("transactions.topup-failed", { ns: "store" }),
              description: AcFormatErrorMessage(error),
            });

          this.setBusy(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    get_refundable = (t, uid) => {
      this.setLoadingRefundable(true);

      return app.store.api.transactions
        .get_refundable(uid)
        .then(async (response) => {
          await this.set(KEYS.REFUNDABLE, response);

          this.setLoadingRefundable(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error))
            app.store.toasters.add({
              variant: "error",
              title: t("transactions.failed-to-retrieve-refundable-data", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
            });

          this.setLoadingRefundable(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    get_payouts = (t, uid) => {
      this.setLoadingRefundPayouts(true);

      return app.store.api.transactions
        .get_payouts(uid)
        .then(async (response) => {
          await this.set(KEYS.PAYOUTS, response);

          this.setLoadingRefundPayouts(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error))
            app.store.toasters.add({
              variant: "error",
              title: t("transactions.failed-to-retrieve-payouts-data", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
            });

          this.setLoadingRefundPayouts(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    get_refund_payouts = (t, uid) => {
      this.setLoadingPayouts(true);

      return app.store.api.transactions
        .get_refund_payouts(uid)
        .then(async (response) => {
          if (
            AcIsSet(response[0]) &&
          AcIsSet(response[0]["refunds"]) &&
          AcIsSet(response[0]["refunds"])
          ) {
            await this.set(KEYS.REFUND_PAYOUTS, response[0]["refunds"]);
          } else {
            await this.set(KEYS.REFUND_PAYOUTS, []);
          }

          if (AcIsSet(response[0])) {
            await this.set(KEYS.REFUND_TRANSACTION, response[0]);
          } else {
            await this.set(KEYS.REFUND_TRANSACTION, []);
          }

          this.setLoadingPayouts(false);

          return response;
        })
        .catch((error) => {
          if (!axios.isCancel(error))
            app.store.toasters.add({
              variant: "error",
              title: t("transactions.failed-to-retrieve-payouts-data", {
                ns: "store",
              }),
              description: AcFormatErrorMessage(error),
            });

          this.setLoadingPayouts(false);

          if (!axios.isCancel(error)) throw error;
        });
    };

  @action
    set = (target, value, save = false) => {
      if (!AcIsSet(target)) return;
      if (AcIsUndefined(this[target])) return;
      if (AcIsUndefined(value)) return;

      return new Promise((resolve) => {
        this[target] = value;
        if (save) AcSaveState(target, value);
        resolve();
      });
    };

  @action
    setState = (target, property, value, save = false) => {
      if (!AcIsSet(target)) return;
      if (AcIsUndefined(this[target])) return;
      if (!AcIsSet(property)) return;
      if (AcIsUndefined(value)) return;

      this[target][property] = value;
      if (save) AcSaveState(target, value);
    };

  @action
    reset = (target, save = false) => {
      if (!AcIsSet(target)) return;
      if (AcIsUndefined(this[target])) return;

      return new Promise((resolve) => {
        this[target] = _default[target];
        if (save && AcIsNull(_default[target])) {
          AcRemoveState(target);
        } else if (save) {
          AcSaveState(target, _default[target]);
        }

        resolve();
      });
    };
}

export default TransactionsStore;
